import { useState, useEffect } from "react";
import { Container, Navbar, Nav } from "react-bootstrap";
import LogoutButton from "../components/LogoutButton";
import ServiceDropdowns from "../components/ServiceDropdowns";
import IConsultTesting from "../components/IConsultTesting";
import P2PTesting from "../components/P2PTesting";
import SSLCertificates from "../components/SSLCertificates";
import Deployments from "../components/Deployments";
import EnvAnalyzer from "../components/EnvAnalyzer";
import { useAuth } from "../auth/AuthContext";
import '../../sass/pages/home.scss'

const Home = () => {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);
  const isSSLOnly = !isAdmin && Boolean(user?.isSslCertViewer);
  const canUseEnvAnalyzer = isAdmin || Boolean(user?.isEnvAnalyzerViewer);

  const [showBugComponent, setShowBugComponent] = useState(false);

  // Initialize activeView from localStorage or default to 'services'
  const [activeView, setActiveView] = useState(() => {
    const savedView = localStorage.getItem('platformapi-active-view');
    console.log('Loading saved view from localStorage:', savedView);
    return savedView || 'services'; // 'services', 'iconsult', 'p2p', 'ssl-certificates', 'deployments', 'env-analyzer'
  });

  // Views a non-admin, role-restricted user is allowed to land on. Admins are
  // unrestricted. A user with no restricting role at all (no group) is also
  // left unrestricted here - the existing per-link nav gating already hides
  // what they shouldn't use.
  const allowedViews = isAdmin
    ? null
    : [
        ...(isSSLOnly ? ['ssl-certificates', 'deployments'] : []),
        ...(canUseEnvAnalyzer ? ['env-analyzer'] : []),
      ];

  // Redirect role-restricted users away from views they can't access
  useEffect(() => {
    if (allowedViews && allowedViews.length > 0 && !allowedViews.includes(activeView)) {
      setActiveView(allowedViews[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSSLOnly, canUseEnvAnalyzer, isAdmin]);

  // Save activeView to localStorage whenever it changes
  useEffect(() => {
    console.log('Saving activeView to localStorage:', activeView);
    localStorage.setItem('platformapi-active-view', activeView);
  }, [activeView]);

  const handleSelectionChange = (environment: any, service: any, endpoint: any) => {
    console.log('Selection changed:', { environment, service, endpoint });
    // You can add additional logic here when selections change
  };
  
  const renderContent = () => {
    if (isSSLOnly) {
      if (activeView === 'deployments') return <Deployments />;
      return <SSLCertificates />;
    }
    switch (activeView) {
      case 'services':
        return <ServiceDropdowns onSelectionChange={handleSelectionChange} />;
      case 'iconsult':
        return <IConsultTesting />;
      case 'p2p':
        return <P2PTesting />;
      case 'ssl-certificates':
        return <SSLCertificates />;
      case 'deployments':
        return <Deployments />;
      case 'env-analyzer':
        return canUseEnvAnalyzer ? <EnvAnalyzer /> : <ServiceDropdowns onSelectionChange={handleSelectionChange} />;
      default:
        return <ServiceDropdowns onSelectionChange={handleSelectionChange} />;
    }
  };

  return (
    <Container fluid className="home-container">
      <Navbar className="header-bar">
        <Container fluid>
          <Navbar.Brand className="header-title">Caregility Platform Operations</Navbar.Brand>
          <Nav className="me-auto">
            {isAdmin && (
              <Nav.Link
                className={`nav-link ${activeView === 'services' ? 'active' : ''}`}
                onClick={() => setActiveView('services')}
              >
                Services API Testing
              </Nav.Link>
            )}
            {isAdmin && (
              <Nav.Link
                className={`nav-link ${activeView === 'iconsult' ? 'active' : ''}`}
                onClick={() => setActiveView('iconsult')}
              >
                iConsult Call Testing
              </Nav.Link>
            )}
            {isAdmin && (
              <Nav.Link
                className={`nav-link ${activeView === 'p2p' ? 'active' : ''}`}
                onClick={() => setActiveView('p2p')}
              >
                P2P Call Testing
              </Nav.Link>
            )}
            <Nav.Link
              className={`nav-link ${activeView === 'ssl-certificates' ? 'active' : ''}`}
              onClick={() => setActiveView('ssl-certificates')}
            >
              SSL Certificates
            </Nav.Link>
            <Nav.Link
              className={`nav-link ${activeView === 'deployments' ? 'active' : ''}`}
              onClick={() => setActiveView('deployments')}
            >
              Deployments
            </Nav.Link>
            {canUseEnvAnalyzer && (
              <Nav.Link
                className={`nav-link ${activeView === 'env-analyzer' ? 'active' : ''}`}
                onClick={() => setActiveView('env-analyzer')}
              >
                Env Analyzer
              </Nav.Link>
            )}
          </Nav>
          <LogoutButton />
        </Container>
      </Navbar>

      {renderContent()}

      {/* Intentional Bug for Sentry */}
      {showBugComponent && (showBugComponent as any).field.notexist}
    </Container>
  );
};

export default Home;
